import React from 'react';
import { Container } from '@mui/material';
import PropTypes from 'prop-types';
import { Box, Typography } from '../atoms';

/**
 * PageContainer Organism
 * A standard page container with consistent layout and header
 * 
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Page content
 * @param {string} props.title - Page title
 * @param {React.ReactNode} props.titleIcon - Icon to display before the title
 * @param {React.ReactNode} props.actions - Actions to display in the header
 * @param {React.ReactNode} props.breadcrumbs - Breadcrumbs to display above the title
 * @param {boolean} props.fullWidth - Whether to use full width container
 * @param {string} props.maxWidth - Maximum width of the container
 * @param {Object} props.sx - Additional styles
 * @returns {React.ReactElement} PageContainer component
 */
const PageContainer = ({
  children,
  title,
  titleIcon,
  actions,
  breadcrumbs,
  fullWidth = false,
  maxWidth = 'lg',
  sx = {},
  ...rest
}) => {
  return (
    <Container 
      maxWidth={fullWidth ? false : maxWidth} 
      sx={{ 
        py: 3, 
        ...sx 
      }}
      {...rest}
    >
      {/* Breadcrumbs */}
      {breadcrumbs && (
        <Box sx={{ mb: 2 }}>
          {breadcrumbs}
        </Box>
      )}
      
      {/* Page Header */}
      {(title || actions) && (
        <Box 
          sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'space-between',
            mb: 3,
            pb: 1.5,
            borderBottom: (theme) => `1px solid ${theme.palette.divider}`
          }}
        >
          {/* Title with optional icon */}
          {title && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {titleIcon && (
                <Box sx={{ display: 'flex', alignItems: 'center', color: 'primary.main' }}>
                  {titleIcon}
                </Box>
              )}
              <Typography variant="h4" component="h1" fontWeight="medium">
                {title}
              </Typography>
            </Box>
          )}
          
          {/* Actions */}
          {actions && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {actions}
            </Box>
          )}
        </Box>
      )}
      
      {/* Page Content */}
      <Box>
        {children}
      </Box>
    </Container>
  );
};

PageContainer.propTypes = {
  children: PropTypes.node,
  title: PropTypes.string,
  titleIcon: PropTypes.node,
  actions: PropTypes.node,
  breadcrumbs: PropTypes.node,
  fullWidth: PropTypes.bool,
  maxWidth: PropTypes.oneOf(['xs', 'sm', 'md', 'lg', 'xl', false]),
  sx: PropTypes.object
};

export default PageContainer;